import { useParams } from 'react-router-dom';
import { useState } from 'react';
import { dateToLocalFormat } from 'helpers/dateToLocalFormat';
import { mediaUrl } from 'helpers/mediaUrl';
import { resolveBannerImage } from 'helpers/eventBanner';
import ErrorState from 'common/ErrorState/ErrorState';
import BackButton from 'common/BackButton/BackButton';
import NotFound from '../../NotFound/NotFound';
import PartnerCard from './PartnerCard/PartnerCard';
import { useEvent } from 'hooks/useEvent';
import Loader from 'components/Loader/Loader';
import './EventInfo.css';
import { useLanguage } from 'hooks/useLanguage';

const TABS = [
  { id: 'partners', ua: 'Спонсори та партнери', en: 'Sponsors and partners' },
  { id: 'hotels', ua: 'Готелі', en: 'Hotels' },
  { id: 'address', ua: 'Адреса', en: 'Address' },
];

/**
 * Сторінка заходу — ADR-0005, ADR-0008.
 *
 * Було: вкладки перемикалися через addEventListener на DOM-вузлах усередині
 * useEffect (слухачі не знімалися), активний стан визначався порівнянням
 * innerText з рядком українською, а індикатор вкладки малювався псевдоелементами
 * з box-shadow, щоб імітувати заокруглення.
 * Стало: звичайний стан React, вкладки з роллю tablist і керуванням з клавіатури.
 *
 * Запис сторінка питає окремим запитом, а не шукає в календарі. Календар
 * свідомо віддає лише майбутні заходи, тому посилання на захід, що вже минув,
 * не знаходило нічого — і сторінка лишалася на лоадері назавжди.
 *
 * Звідки саме береться захід — зі змагань e-phan чи з CMS — вирішує useEvent
 * (ADR-0012). Сторінка бачить один запис.
 */
export default function EventInfo() {
  const language = useLanguage();
  const [activeTab, setActiveTab] = useState('partners');
  const { eventSlug } = useParams();

  const { data: event, status, error, retry } = useEvent(eventSlug);

  if (status === 'loading') return <Loader />;

  // Неіснуючий захід — це не збій завантаження, а звичайна 404 сторінка.
  if (error?.kind === 'notFound') return <NotFound />;

  if (status === 'failed') {
    return (
      <div className='container section'>
        <ErrorState error={error} onRetry={retry} variant='page' />
      </div>
    );
  }

  if (!event) return <NotFound />;

  const {
    banner,
    title,
    organizator,
    town,
    link,
    judges,
    registration,
    partners,
    hotels,
    address,
    venue,
    entry,
    start,
    end,
  } = event;

  const notSpecified = language === 'en' ? 'Not specified' : 'Не вказано';

  const dates = end
    ? `${dateToLocalFormat(start).slice(0, 5)} - ${dateToLocalFormat(end).slice(0, 5)}`
    : dateToLocalFormat(start).slice(0, 5);

  const infoHref = link || mediaUrl(entry?.url);
  const bannerImage = resolveBannerImage(banner, 'vertical');
  // Організації заходу показуються в тому самому переліку, що й спонсори:
  // для відвідувача це однаково логотип із назвою й посиланням.
  //
  // Ключ склеєний із розділу та id: це два різні набори записів, і організація
  // №1 цілком може співіснувати з партнером №1. Раніше ключем був самий id,
  // тож такий збіг ламав React-узгодження в цьому списку.
  const allPartners = [
    ...(event.organizations || []).map((item) => ({ ...item, key: `org-${item.id}` })),
    ...(partners || []).map((item) => ({ ...item, key: `partner-${item.id}` })),
  ];

  return (
    <article className='event-info section'>
      <div className='container'>
        <BackButton />

        <div className='event-detail-info-row'>
          <div className='img-wrapper'>
            {bannerImage ? (
              <img
                className='event-banner'
                src={bannerImage.src}
                srcSet={bannerImage.srcSet}
                sizes='(max-width: 760px) 100vw, 320px'
                alt={language === 'en' ? 'Event banner' : 'Банер турніру'}
              />
            ) : (
              <span className='banner-placeholder' aria-hidden='true' />
            )}
          </div>

          <div className='event-details'>
            <h1 className='event-detail-info-title'>{title}</h1>

            {event.source === 'ephan' && (
              <p className='event-source-note'>
                {language === 'en' ? 'Data from e-phan' : 'Дані з e-phan'}
                {event.externalUrl && (
                  <a href={event.externalUrl} target='_blank' rel='noreferrer'>
                    {language === 'en' ? 'Open the original' : 'Відкрити першоджерело'}
                  </a>
                )}
              </p>
            )}

            <dl className='event-detail-list'>
              <div>
                <dt>{language === 'en' ? 'Date' : 'Дата'}</dt>
                <dd className='tnum'>{dates}</dd>
              </div>
              <div>
                <dt>{language === 'en' ? 'City' : 'Місто'}</dt>
                <dd>{town || notSpecified}</dd>
              </div>
              {venue && (
                <div>
                  <dt>{language === 'en' ? 'Venue' : 'Місце проведення'}</dt>
                  <dd>{venue}</dd>
                </div>
              )}
              <div>
                <dt>{language === 'en' ? 'Organizer' : 'Організатор'}</dt>
                {/* Заходи з e-phan приходять без організатора: у джерелі такого
                    поля немає, і вигадувати його для офіційного календаря не
                    можна. */}
                <dd>{organizator || notSpecified}</dd>
              </div>
              <div>
                <dt>{language === 'en' ? 'Organization' : 'Організація'}</dt>
                <dd>
                  {event.organizations?.length
                    ? event.organizations.map(({ name }) => name).join(', ')
                    : language === 'en'
                    ? 'Not specified'
                    : 'Не вказано'}
                </dd>
              </div>
            </dl>

            <div className='event-actions'>
              <a
                target='_blank'
                rel='noreferrer'
                href={infoHref || undefined}
                aria-disabled={!infoHref}
                className={`btn btn--secondary event-info-btn ${!infoHref ? 'is-disabled' : ''}`}
              >
                {language === 'en' ? 'Information' : 'Інформація'}
              </a>
              <a
                target='_blank'
                rel='noreferrer'
                href={judges || undefined}
                aria-disabled={!judges}
                className={`btn btn--secondary event-info-btn ${!judges ? 'is-disabled' : ''}`}
              >
                {language === 'en' ? 'Judges' : 'Судді'}
              </a>
              <a
                target='_blank'
                rel='noreferrer'
                href={registration || undefined}
                aria-disabled={!registration}
                className={`btn btn--primary event-info-btn ${!registration ? 'is-disabled' : ''}`}
              >
                {language === 'en' ? 'Registration' : 'Реєстрація учасників'}
              </a>
            </div>
          </div>
        </div>

        <div className='event-detail-buttons' role='tablist'>
          {TABS.map((tab) => (
            <button
              type='button'
              role='tab'
              id={`tab-${tab.id}`}
              aria-selected={activeTab === tab.id}
              aria-controls={`panel-${tab.id}`}
              key={tab.id}
              className={`event-details-list ${activeTab === tab.id ? 'is-active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              {language === 'en' ? tab.en : tab.ua}
            </button>
          ))}
        </div>

        <div
          key={activeTab}
          className='event-detail-information'
          role='tabpanel'
          id={`panel-${activeTab}`}
          aria-labelledby={`tab-${activeTab}`}
          tabIndex={0}
        >
          {activeTab === 'partners' &&
            (allPartners.length > 0 ? (
              <div className='partners-wrapper'>
                {allPartners.map((partner) => (
                  <PartnerCard key={partner.key} partner={partner} />
                ))}
              </div>
            ) : (
              <p className='empty-note'>
                {language === 'en' ? 'Partners are not specified.' : 'Партнерів не вказано.'}
              </p>
            ))}

          {activeTab === 'hotels' &&
            (hotels ? (
              <iframe
                className='event-map'
                title={language === 'en' ? 'Hotels map' : 'Карта готелів'}
                src={hotels}
                loading='lazy'
                referrerPolicy='no-referrer-when-downgrade'
              />
            ) : (
              <p className='empty-note'>
                {language === 'en' ? 'Hotels are not specified.' : 'Готелі не вказано.'}
              </p>
            ))}

          {/* Карта, якщо редактор вставив її адресу; інакше текстова адреса,
              як вона приходить із e-phan. Класти текстову адресу в src фрейма
              не можна — вийшов би зламаний фрейм замість карти. */}
          {activeTab === 'address' &&
            (address ? (
              <iframe
                className='event-map'
                title={language === 'en' ? 'Venue map' : 'Карта місця проведення'}
                src={address}
                loading='lazy'
                referrerPolicy='no-referrer-when-downgrade'
              />
            ) : venue ? (
              <p className='event-venue'>{venue}</p>
            ) : (
              <p className='empty-note'>
                {language === 'en' ? 'The address is not specified.' : 'Адресу не вказано.'}
              </p>
            ))}
        </div>
      </div>
    </article>
  );
}
